import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { DropZone } from '@/components/DropZone';
import { FarmerPhoto } from '@/components/FarmerPhoto';
import { Drawer, Field, Select } from '@/components/live';
import { callRpc } from '@/api/resources';
import { uploadDocument } from '@/api/documents';
import { normaliseIdentifier } from '@/auth/session';
import { useAuth } from '@/auth/AuthProvider';
import { toast } from '@/lib/toast';

const PAPER_METHODS = [
  { id: 'signature', label: 'Signed by the farmer' },
  { id: 'thumb', label: 'Thumb impression' },
  { id: 'manual_witness', label: 'Witnessed in person' },
];

/**
 * The print route of a consent (owner, 2026-09-28: "both digital and print as standard"): the
 * farmer - and on a family, sharecropping or representative consent everyone named with them -
 * signs the two-language print copy before a witness; the officer uploads the signed copy and
 * records how it was given. The database refuses the acknowledgement without that copy.
 *
 * A signed copy already dropped on the Documents row can be used instead of uploading again.
 */
export function PaperConsent({ consentId, partyId, fileBase, existingSignedId, onOpenPrintCopy, onClose, onRecorded }: {
  consentId: string; partyId: string;
  /** "plot-consent-PLT-000001-en-kn": the signed copy is kept as "<fileBase>-paper.<ext>". */
  fileBase: string;
  existingSignedId?: string | null;
  onOpenPrintCopy: () => void;
  onClose: () => void;
  onRecorded: () => void;
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [method, setMethod] = useState('signature');
  const [witness, setWitness] = useState('');
  const [witnessPhone, setWitnessPhone] = useState('');
  const [witnessPhotoId, setWitnessPhotoId] = useState<string | null>(null);
  const [witnessPreview, setWitnessPreview] = useState<string | null>(null);
  const [signedId, setSignedId] = useState<string | null>(existingSignedId ?? null);
  const [busy, setBusy] = useState(false);

  async function attach(file: File, kind: 'signed' | 'witness') {
    if (!profile?.organization) return;
    if (kind === 'witness' && !file.type.startsWith('image/')) { toast.error('Choose a photograph of the witness.'); return; }
    setBusy(true);
    try {
      const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : 'pdf';
      const named = kind === 'signed' ? new File([file], `${fileBase}-paper.${extension}`, { type: file.type }) : file;
      const stored = await uploadDocument({ file: named, bucket: 'generated-documents', organizationId: profile.organization.id, ownerId: partyId });
      await callRpc('fn_link_document', {
        p_document_id: stored.id, p_entity_table: 'party.party_consents', p_entity_id: consentId, p_link_type: kind === 'signed' ? 'primary' : 'supporting',
      });
      if (kind === 'signed') setSignedId(stored.id);
      else {
        setWitnessPhotoId(stored.id);
        setWitnessPreview((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(file); });
      }
      await queryClient.invalidateQueries({ queryKey: ['record_documents'] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The file could not be attached.');
    } finally {
      setBusy(false);
    }
  }

  async function record() {
    if (!signedId) { toast.error('Upload the signed copy first.'); return; }
    let phone: string | null = null;
    if (witnessPhone.trim()) {
      const result = normaliseIdentifier('sms', witnessPhone.trim());
      if ('error' in result) { toast.error(`Witness phone: ${result.error}`); return; }
      phone = result.value;
    }
    if (method !== 'signature' && !witness.trim()) { toast.error('A thumb impression or witnessed consent needs the witness\'s name.'); return; }
    setBusy(true);
    try {
      await callRpc('fn_acknowledge_consent_signed', {
        p_consent_id: consentId, p_document_id: signedId, p_method: method,
        p_witness_name: witness.trim() || null, p_witness_phone: phone, p_witness_photo_document_id: witnessPhotoId,
      });
      await queryClient.invalidateQueries({ queryKey: ['party_consents'] });
      await queryClient.invalidateQueries({ queryKey: ['record_documents'] });
      toast.success('Consent recorded from the signed copy.');
      onRecorded();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The consent could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      footer={<>
        <button className="cancel" onClick={onClose} type="button">Cancel</button>
        <button className="primary" disabled={busy || !signedId} onClick={() => void record()} type="button">{busy ? 'Recording...' : 'Record consent'}</button>
      </>}
      onClose={onClose} open subtitle="Signed by hand or thumb, before a witness." title="Consent on signed paper"
    >
      <ol className="paper-steps">
        <li>
          Print the print copy (English and the farmer&apos;s language) and read it to the farmer.
          <button onClick={onOpenPrintCopy} type="button"><Printer size={14} /> Open the print copy</button>
        </li>
        <li>The farmer, and everyone named on it, signs or gives a thumb impression on every page, before a witness.</li>
        <li>
          Upload the signed copy (scan or photos in one PDF).
          <DropZone accept="application/pdf,image/jpeg,image/png" busy={busy} buttonLabel={signedId ? 'Choose another' : 'Choose file'}
                    label={signedId ? 'Signed copy attached. Drop another to replace, or' : 'Drag the signed copy here, or'}
                    onFile={(file) => void attach(file, 'signed')} />
        </li>
      </ol>
      <Field label="How the farmer acknowledged">
        <Select onChange={setMethod} options={PAPER_METHODS} value={method} />
      </Field>
      <div className="section-heading"><h3>Witness</h3></div>
      <div className="field-row">
        <Field label="Witness name" required={method !== 'signature'}>
          <input onChange={(event) => setWitness(event.target.value)} value={witness} />
        </Field>
        <Field label="Witness phone">
          <input inputMode="tel" onChange={(event) => setWitnessPhone(event.target.value)} placeholder="10 digits" value={witnessPhone} />
        </Field>
      </div>
      <Field label="Witness photograph">
        <DropZone accept="image/*" busy={busy} buttonLabel={witnessPhotoId ? 'Choose another' : 'Choose photo'}
                  label={witnessPhotoId ? 'Photograph attached. Drop another to replace, or' : 'Drag a photo of the witness here, or'}
                  onFile={(file) => void attach(file, 'witness')}>
          {witnessPreview && <FarmerPhoto name={witness} size={64} url={witnessPreview} />}
        </DropZone>
      </Field>
    </Drawer>
  );
}
